import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import formatISO from "date-fns/formatISO";
import parseISO from "date-fns/parseISO";
import { FormikProvider, useFormik } from "formik";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import React, { ChangeEvent, SyntheticEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useRouteMatch } from "react-router-dom";

import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Link from "@material-ui/core/Link";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import ValidIcon from "@material-ui/icons/CheckCircle";
import InvalidIcon from "@material-ui/icons/Warning";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { EMPTY_STRING } from "common/constants";
import {
  CITY_REGEXP,
  ONLY_NUMBERS_REGEXP,
  POLICY_NUMBER_REGEXP,
  STREET_ADDRESS_REGEXP
} from "common/regExp";
import routes from "common/routes";
import AppPage from "components/AppPage";
import AppPageContent from "components/AppPageContent";
import AppPageHeader from "components/AppPageHeader";
import { useClickBlockerContext } from "components/BlockableClickContext";
import ErrorBoundary from "components/ErrorBoundary";
import Form from "components/Form";
import Loader from "components/Loader";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import { useSnackbar } from "components/SnackbarContext/SnackbarContext";
import StallApplicationReview from "components/StallApplicationReview";
import SwipeableTabs from "components/SwipeableTabs";
import UnsavedChangesDialog from "components/UnsavedChangesDialog";
import { useRacehorse360Api } from "hooks/api";
import {
  setActiveLink,
  setStoreActiveLinkCandidate
} from "store/actions/sidebar";
import { transformViewDate } from "utils/date-utils";
import { getPersonFullName } from "utils/person";
import { getRequiredValue } from "utils/required-values-utils";
import FormHeader from "./FormHeader";
import FormModule from "./FormModule";
import { MobileFormHeader } from "./MobileViewComponents";
import StablingEntries from "./StablingEntries";
import TermsAndConditionsPopup from "./TermsAndConditionsPopup";
import {
  FOCUS_DELAY,
  FormFieldName,
  getModules,
  IApplicationFieldsState,
  ILocationState,
  initialState,
  STABLE_ENTRIES_INPUT,
  Tabs,
  UNSAVED_CHANGES_TEXT
} from "./constants";
import useStyles from "./styles";
import validationSchema from "./validationSchema";

const StallApplicationFormPage = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory<ILocationState>();
  const match = useRouteMatch<{ stallApplicationFormId: string }>(
    routes.stallApplicationTrainerForm.path
  );
  const matchesDownSm = useMediaQuery(theme.breakpoints.down("sm"), {
    noSsr: true
  });
  const dispatch = useDispatch();
  const { currentUser } = useLoggedInUser();
  const queryClient = useQueryClient();
  const { showSuccessSnack, showErrorSnack } = useSnackbar();
  const linkCandidate = useSelector(
    (state: { sidebar }) => state?.sidebar.activeLinkCandidate
  );

  const [showValidationResults, setShowValidationResults] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(Tabs[0]);
  const [showReview, setShowReview] = useState<boolean>(false);
  const [showTermsAndConditionsPopup, setShowTermsAndConditionsCPopup] =
    useState<boolean>(false);
  const [tncAgree, setTncAgree] = useState<boolean>(false);
  const [stateStallApplication, setStateStallApplication] =
    useState<racehorse360.IStallApplication>();
  const [originalStableEntries, setOriginalStablingEntries] = useState<
    racehorse360.IStableEntry[]
  >([]);
  const [pickedStableEntries, setPickedStablingEntries] = useState<
    racehorse360.IStableEntry[]
  >([]);
  const [filledModules, setFilledModules] = useState(
    getModules(currentUser, {})
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedChangesPopup, setShowUnsavedChangesPopup] = useState(false);
  const [shouldValidateOnChange, setShouldValidateOnChange] = useState(false);
  const [shouldExpandBlock, setShouldExpandBlock] = useState(false);
  const [tempState, setTempState] = useState<IApplicationFieldsState>(null);

  const { setBlockClick, setBlockClickCallback } = useClickBlockerContext();
  const stallApplicationFormId = match.params.stallApplicationFormId;
  const hasEntriesValidationError = [
    !pickedStableEntries.length,
    shouldValidateOnChange
  ].every(Boolean);

  const redirectToLinkCandidate = () => {
    if (linkCandidate) {
      dispatch(setActiveLink(linkCandidate));
      dispatch(setStoreActiveLinkCandidate(EMPTY_STRING));
      history.push(linkCandidate);
    }
  };

  const clearLinkCandidate = () => {
    dispatch(setStoreActiveLinkCandidate(EMPTY_STRING));
  };

  const lockNavigation = () => {
    setBlockClickCallback(() => () => setShowUnsavedChangesPopup(true));
    setBlockClick(true);
  };

  const unlockNavigation = () => {
    setBlockClick(false);
    setBlockClickCallback(null);
    redirectToLinkCandidate();
  };

  const {
    useListTrainers,
    useCreateStallApplication,
    useUpdateStallApplication,
    useSubmitStallApplication,
    useListStallApplications,
    useListStableEntries,
    useGetStallApplicationForm,
    useSetFacilityPreferences,
    useCreateStableEntry,
    useDeleteStableEntry,
    useUpdateStableEntry
  } = useRacehorse360Api();

  const {
    mutateAsync: createStallApplication,
    isPending: isStallApplicationCreating
  } = useCreateStallApplication();

  const {
    mutateAsync: updateStallApplication,
    isPending: isStallApplicationUpdating
  } = useUpdateStallApplication();

  const {
    mutateAsync: submitStallApplication,
    isPending: isSubmitStallApplicationLoading
  } = useSubmitStallApplication();

  const {
    isPending: isCreateStableEntryLoading,
    mutateAsync: createStableEntry
  } = useCreateStableEntry();

  const {
    isPending: isDeleteStableEntryLoading,
    mutateAsync: deleteStableEntry
  } = useDeleteStableEntry();

  const {
    isPending: isUpdateStableEntryLoading,
    mutateAsync: updateStableEntry
  } = useUpdateStableEntry();

  const {
    isPending: isSetFacilityPreferencesLoading,
    mutateAsync: setFacilityPreferences
  } = useSetFacilityPreferences();

  const listKey = "list-stall-applications";

  const scrollToError = (errors: string[]) => {
    let firstErrorElement: HTMLElement;

    if (errors.length) {
      if (matchesDownSm) {
        setActiveTab(Tabs[0]);
      }
      firstErrorElement = document.querySelector(`[name=${errors[0]}]`);
    } else {
      firstErrorElement = document.querySelector(
        `[name=${STABLE_ENTRIES_INPUT}]`
      );
    }

    const parentBlockOfFirstErrorElement =
      firstErrorElement.closest(".MuiCollapse-root");

    parentBlockOfFirstErrorElement.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
    setTimeout(() => firstErrorElement?.focus(), FOCUS_DELAY);
  };

  const getQueryForUser = () => {
    const queryTrainer = {
      trainerIds: currentUser.trainerIds,
      stallApplicationFormId
    };
    const querySecretary = {
      createdByIds: [currentUser.rh360Id],
      stallApplicationFormId,
      statuses: [
        racehorse360.StallApplicationStatus.STALL_APPLICATION_STATUS_IN_PROGRESS
      ]
    };
    return currentUser.isTrainer ? queryTrainer : querySecretary;
  };

  const { data: stallApplicationForm } = useGetStallApplicationForm(
    {
      id: stallApplicationFormId,
      getOptions: {
        select: [
          "title",
          "start_date",
          "deadline_date",
          "close_date",
          "facility_options:id",
          "facility_options:name",
          "facility_options:code",
          "facility_options:background_color",
          "facility_options:foreground_color",
          "facility_options:stroke_color",
          "facility_options:type",
          "facility_options:is_track_facility"
        ]
      }
    },
    {
      enabled: Boolean(stallApplicationFormId),
      onSuccess: async (form: racehorse360.IStallApplicationForm) => {
        setFilledModules(getModules(currentUser, form));

        //TODO: Need to rework after implementation story RAC-3528 (initial
        // preferences setting should be moved to initial state in useFormik hook)
        const defaultFacilityChoiceId =
          form.facilityOptions.find(f => f.isTrackFacility)?.id ||
          form.facilityOptions[0].id;

        await formik.setFieldValue("preferencesFirst", defaultFacilityChoiceId);
        await formik.setFieldValue(
          "preferencesSecond",
          defaultFacilityChoiceId
        );
      }
    }
  );

  const {
    isFetching: isListStallApplicationsFetching,
    isLoading: isListStallApplicationsLoading
  } = useListStallApplications(
    listKey,
    {
      query: getQueryForUser(),
      getOptions: {
        select: [
          "arrival_date",
          "sleeping_rooms_number",
          "contact_full_name",
          "contact_phone",
          "contact_email",
          "contact_zip_code",
          "contact_state",
          "contact_city",
          "contact_street_address",
          "contact_street_address_optional",
          "carrier_name",
          "policy_number",
          "policy_expiration_date",
          "carrier_zip_code",
          "carrier_state",
          "carrier_city",
          "carrier_street_address",
          "carrier_street_address_optional",
          "is_stable_planned_to_split",
          "comment",
          "first_choice_facility",
          "second_choice_facility",
          "stall_application_form_id",
          "trainer.id"
        ]
      }
    },
    {
      enabled: Boolean(stallApplicationForm),
      onSuccess: async data => {
        const stallApplication = data.stallApplications[0];
        let defaultState = cloneDeep(formik.values);
        setStateStallApplication(stallApplication);

        if (stallApplication) {
          const updatedState = {
            ...stallApplication,
            arrivalDate: stallApplication.arrivalDate
              ? parseISO(stallApplication.arrivalDate)
              : null,
            sleepingRoomsNumber: getRequiredValue(
              stallApplication.sleepingRoomsNumber,
              0
            ),
            contactFullName: getRequiredValue(
              stallApplication.contactFullName,
              EMPTY_STRING
            ),
            contactPhone: getRequiredValue(
              stallApplication.contactPhone,
              EMPTY_STRING
            ),
            contactEmail: getRequiredValue(
              stallApplication.contactEmail,
              EMPTY_STRING
            ),
            contactStreetAddress: getRequiredValue(
              stallApplication.contactStreetAddress,
              EMPTY_STRING
            ),
            contactStreetAddressOptional: getRequiredValue(
              stallApplication.contactStreetAddressOptional,
              EMPTY_STRING
            ),
            contactCity: getRequiredValue(
              stallApplication.contactCity,
              EMPTY_STRING
            ),
            contactState: getRequiredValue(
              stallApplication.contactState,
              EMPTY_STRING
            ),
            contactZipCode: getRequiredValue(
              stallApplication.contactZipCode,
              EMPTY_STRING
            ),
            carrierName: getRequiredValue(
              stallApplication.carrierName,
              EMPTY_STRING
            ),
            policyExpirationDate: stallApplication.policyExpirationDate
              ? parseISO(stallApplication.policyExpirationDate)
              : null,
            policyNumber: getRequiredValue(
              stallApplication.policyNumber,
              EMPTY_STRING
            ),
            carrierStreetAddress: getRequiredValue(
              stallApplication.carrierStreetAddress,
              EMPTY_STRING
            ),
            carrierStreetAddressOptional: getRequiredValue(
              stallApplication.carrierStreetAddressOptional,
              EMPTY_STRING
            ),
            carrierCity: getRequiredValue(
              stallApplication.carrierCity,
              EMPTY_STRING
            ),
            carrierState: getRequiredValue(
              stallApplication.carrierState,
              EMPTY_STRING
            ),
            carrierZipCode: getRequiredValue(
              stallApplication.carrierZipCode,
              EMPTY_STRING
            ),
            isStablePlannedToSplit:
              stallApplication.isStablePlannedToSplit?.value,
            preferencesFirst: getRequiredValue(
              stallApplication.firstChoiceFacility?.id,
              EMPTY_STRING
            ),
            preferencesSecond: getRequiredValue(
              stallApplication.secondChoiceFacility?.id,
              EMPTY_STRING
            ),
            comment: getRequiredValue(stallApplication.comment, EMPTY_STRING)
          };

          defaultState = cloneDeep(updatedState);
          await formik.setValues(updatedState);
        }

        setTempState(defaultState);
      }
    }
  );

  const {
    isFetching: isListStableEntriesFetching,
    isLoading: isListStableEntriesLoading
  } = useListStableEntries(
    {
      query: {
        stallApplicationIds: [stateStallApplication?.id]
      },
      getOptions: {
        select: [
          "horseId",
          "stallApplicationId",
          "customHorseRegistrationNumber",
          "customHorseName",
          "raceReadyDate",
          "horse.name",
          "horse.registrationNumber",
          "horse.breeder.name",
          "horse.owner.fullName"
        ]
      }
    },
    {
      enabled: Boolean(stateStallApplication),
      onSuccess: async data => {
        const stableEntries = data.stableEntries || [];
        const preparedStableEntries = stableEntries.map(entry => ({
          ...entry,
          raceReadyDate: entry.raceReadyDate
            ? transformViewDate(entry.raceReadyDate, null, "yyyy-MM-dd")
            : entry.raceReadyDate,
          horseId: entry.horseId
        }));

        setOriginalStablingEntries(preparedStableEntries);
        setPickedStablingEntries(preparedStableEntries);
      }
    }
  );

  const {
    isFetching: isListTrainersFetching,
    isLoading: isListTrainersLoading
  } = useListTrainers(
    {
      query: {
        ids: currentUser.trainerIds
      },
      getOptions: {
        select: ["firstName", "middleName", "lastName"]
      }
    },
    {
      enabled: currentUser.isTrainer || currentUser.isAssistantTrainer,
      onSuccess: async data => {
        const trainer = data.trainers[0];
        await formik.setFieldValue(
          "contactFullName",
          getPersonFullName(trainer)
        );
      }
    }
  );

  const isLoading = [
    isListStallApplicationsFetching,
    isListStallApplicationsLoading,
    isListStableEntriesFetching,
    isListStableEntriesLoading,
    isListTrainersLoading
  ].some(Boolean);

  const isFetching = [
    isListTrainersFetching,
    isCreateStableEntryLoading,
    isDeleteStableEntryLoading,
    isUpdateStableEntryLoading,
    isSubmitStallApplicationLoading,
    isStallApplicationCreating,
    isStallApplicationUpdating,
    isSetFacilityPreferencesLoading
  ].some(Boolean);

  const saveStablingEntries = (stallApplicationId: string) => {
    const promises = [];
    const newStableEntries = pickedStableEntries.filter(se => !se.id);
    const updatedStableEntries = pickedStableEntries.filter(se => se.id);
    const updatedStableEntriesIds = new Set(
      updatedStableEntries.map(se => se.id)
    );
    const deletedStableEntries = originalStableEntries.filter(
      se => !updatedStableEntriesIds.has(se.id)
    );

    newStableEntries.forEach(stableEntry => {
      promises.push(
        createStableEntry({
          stableEntry: {
            ...stableEntry,
            stallApplicationId,
            horseId: stableEntry.customHorseName
              ? undefined
              : stableEntry.horseId,
            horse: undefined
          }
        })
      );
    });

    updatedStableEntries.forEach(stableEntry => {
      promises.push(
        updateStableEntry({
          stableEntry,
          updateMask: {
            paths: ["raceReadyDate"]
          }
        })
      );
    });

    deletedStableEntries.forEach(stableEntry => {
      promises.push(
        deleteStableEntry({
          id: stableEntry.id
        })
      );
    });

    return Promise.all(promises);
  };

  const saveFacilityPreferences = (stallApplicationId: string) => {
    if (values.preferencesFirst || values.preferencesSecond) {
      return setFacilityPreferences({
        stallApplicationId,
        firstChoiceFacilityId: values.preferencesFirst,
        secondChoiceFacilityId: values.preferencesSecond
      });
    } else {
      return Promise.resolve(racehorse360.EmptyResponse.create({}));
    }
  };

  const saveAndClose =
    (isSubmit: boolean = false) =>
    () => {
      const stallApplication = {
        ...values,
        arrivalDate: values.arrivalDate
          ? formatISO(values.arrivalDate, {
              representation: "date"
            })
          : null,
        policyExpirationDate: values.policyExpirationDate
          ? formatISO(values.policyExpirationDate, {
              representation: "date"
            })
          : null,
        isStablePlannedToSplit:
          typeof values.isStablePlannedToSplit === "boolean"
            ? { value: values.isStablePlannedToSplit }
            : null,
        trainerId: currentUser.trainerId,
        preferencesFirst: undefined,
        preferencesSecond: undefined,
        stallApplicationFormId
      };

      let stallApplicationId = stateStallApplication?.id;

      if (stallApplicationId) {
        setShowUnsavedChangesPopup(false);

        updateStallApplication({
          stallApplication,
          updateMask: {
            paths: Object.keys(initialState).filter(
              path => !["preferencesFirst", "preferencesSecond"].includes(path)
            )
          }
        })
          .then(() =>
            Promise.all([
              saveFacilityPreferences(stallApplicationId),
              saveStablingEntries(stallApplicationId)
            ])
          )
          .then(async () => {
            if (isSubmit) {
              await submitStallApplication({ stallApplicationId });
            }

            isSubmit && showSuccessSnack("Application submitted successfully.");
            navigateAfterLeave();
          })
          .finally(() => {
            return queryClient.invalidateQueries({
              queryKey: [listKey]
            });
          })
          .catch(() => {
            isSubmit &&
              showErrorSnack(
                "Application could not be submitted. Please try again."
              );
          });
      } else {
        createStallApplication({
          stallApplication
        })
          .then(createdStallApplication => {
            stallApplicationId = createdStallApplication.id;
            setStateStallApplication(createdStallApplication);

            return Promise.all([
              saveFacilityPreferences(stallApplicationId),
              saveStablingEntries(stallApplicationId)
            ]);
          })
          .then(async () => {
            if (isSubmit) {
              await submitStallApplication({ stallApplicationId });
              showSuccessSnack("Application submitted successfully.");
            }

            navigateAfterLeave();

            return queryClient.invalidateQueries({
              queryKey: [listKey]
            });
          })
          .catch(() => {
            isSubmit &&
              showErrorSnack(
                "Application could not be submitted. Please try again."
              );
          });
      }
    };

  const formik = useFormik<IApplicationFieldsState>({
    initialValues: initialState,
    validationSchema: validationSchema,
    onSubmit: saveAndClose(),
    validateOnMount: false,
    validateOnBlur: false,
    validateOnChange: shouldValidateOnChange
  });
  const values = formik.values || {};

  const handleValueChange = async (
    fieldName: string,
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    if (!event.target) {
      await formik.setFieldValue(fieldName, event);
      return;
    }

    const currentValue = event.target.value;
    let actualRegExp = /./;

    if (
      fieldName === FormFieldName.CONTACT_STREET_ADDRESS ||
      fieldName === FormFieldName.CARRIER_STREET_ADDRESS
    ) {
      actualRegExp = STREET_ADDRESS_REGEXP;
    } else if (
      fieldName === FormFieldName.CONTACT_CITY ||
      fieldName === FormFieldName.CARRIER_CITY
    ) {
      actualRegExp = CITY_REGEXP;
    } else if (
      fieldName === FormFieldName.CONTACT_ZIP_CODE ||
      fieldName === FormFieldName.CARRIER_ZIP_CODE
    ) {
      actualRegExp = ONLY_NUMBERS_REGEXP;
    } else if (fieldName === FormFieldName.POLICY_NUMBER) {
      actualRegExp = POLICY_NUMBER_REGEXP;
    }

    if (!currentValue || actualRegExp.test(currentValue)) {
      await formik.setFieldValue(fieldName, currentValue);
    }
  };

  const handleFormValueChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    if (e.target.name === FormFieldName.SLEEPING_ROOMS_NUMBER) {
      formik.setFieldValue(e.target.name, Number(e.target.value));
    } else {
      formik.handleChange(e);
    }

    setShouldExpandBlock(false);
  };

  const handleStableEntriesChange = (entries: racehorse360.IStableEntry[]) => {
    setPickedStablingEntries(entries);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const handleNextClick = () => {
    setActiveTab(Tabs[1]);
  };

  const handleBackClick = () => {
    if (showReview) {
      setShowReview(false);
    } else {
      setActiveTab(Tabs[0]);
    }
  };

  const handleShowReviewClick = () => {
    formik
      .validateForm()
      .then(errorsObject => {
        const errors = Object.keys(errorsObject);

        if (!errors.length && pickedStableEntries.length) {
          setShowReview(true);
          setShouldValidateOnChange(false);
          return;
        }
        if (errors.length) {
          formik.setErrors(errorsObject);
          setShowValidationResults(true);
          setShouldExpandBlock(true);
          scrollToError(errors);
        }

        setShouldValidateOnChange(true);
      })
      .catch(error => {
        console.error(error);
      });
  };

  const handleTncAgreeCheck = event => {
    setTncAgree(event.currentTarget.checked);
  };

  const navigateAfterLeave = () => {
    if (linkCandidate) {
      redirectToLinkCandidate();
    } else {
      history.goBack();
    }
  };

  const handleTermsAndConditionsLinkClick = (e: SyntheticEvent) => {
    e.preventDefault();
    setShowTermsAndConditionsCPopup(true);
  };

  const handleTermsAndConditionsPopupClose = () => {
    setShowTermsAndConditionsCPopup(false);
  };

  const handleSubmitApplication = () => {
    saveAndClose(true)();
  };

  const handleCancelUnsavedChanges = () => {
    clearLinkCandidate();
    setShowUnsavedChangesPopup(false);
  };

  const getValidationResults = (moduleName: string) => {
    if (!showValidationResults) return null;

    const moduleFields = filledModules
      .find(m => m.name === moduleName)
      .fields.map(m => m.name);
    const hasError = moduleFields.some(f => formik.errors[f]);

    return {
      hasError,
      validationIcon: hasError ? (
        <InvalidIcon
          className={clsx(classes.sectionValidIcon, classes.invalid)}
        />
      ) : (
        <ValidIcon className={clsx(classes.sectionValidIcon, classes.valid)} />
      )
    };
  };

  const renderHeaderContent = () => {
    if (matchesDownSm) {
      return (
        <MobileFormHeader
          hasUnsavedChanges={hasUnsavedChanges}
          onSetShowUnsavedChangesPopup={setShowUnsavedChangesPopup}
          subTitle={stallApplicationForm?.title}
        />
      );
    }

    return (
      <FormHeader
        title={stallApplicationForm?.title}
        deadlineDate={stallApplicationForm?.deadlineDate}
      />
    );
  };

  const renderTrainerDetails = () => {
    return filledModules.map(module => (
      <FormModule
        key={module.name}
        moduleData={module}
        getValidationResults={getValidationResults}
        onValueChange={handleValueChange}
        onFormValueChange={handleFormValueChange}
        shouldExpandBlock={shouldExpandBlock}
        onSetShouldExpandBlock={setShouldExpandBlock}
      />
    ));
  };

  const renderSubmitAndBackButtons = () => (
    <>
      <Button
        variant={"outlined"}
        className={clsx(classes.pageFooterButton)}
        startIcon={<ArrowBackIosIcon />}
        onClick={handleBackClick}
      >
        BACK
      </Button>
      <Button
        className={clsx(classes.pageFooterButton, classes.pageFooterButtonNext)}
        variant={"contained"}
        disableElevation
        disabled={!formik.isValid || !pickedStableEntries.length || !tncAgree}
        onClick={handleSubmitApplication}
      >
        SUBMIT FORM
      </Button>
    </>
  );

  const renderFooter = () => {
    if (matchesDownSm) {
      return showReview ? (
        renderSubmitAndBackButtons()
      ) : (
        <>
          {activeTab === Tabs[0] ? (
            <>
              <Button
                variant={"outlined"}
                className={clsx(classes.pageFooterButton)}
                onClick={saveAndClose()}
              >
                SAVE & CLOSE
              </Button>
              <Button
                className={clsx(
                  classes.pageFooterButton,
                  classes.pageFooterButtonNext
                )}
                variant={"contained"}
                disableElevation
                endIcon={<ArrowForwardIosIcon />}
                onClick={handleNextClick}
              >
                NEXT
              </Button>
            </>
          ) : (
            <>
              <Button
                variant={"outlined"}
                className={clsx(classes.pageFooterButton)}
                startIcon={<ArrowBackIosIcon />}
                onClick={handleBackClick}
              >
                BACK
              </Button>
              <Button
                className={clsx(
                  classes.pageFooterButton,
                  classes.pageFooterButtonNext
                )}
                variant={"contained"}
                disableElevation
                onClick={handleShowReviewClick}
              >
                REVIEW FORM
              </Button>
            </>
          )}
        </>
      );
    } else {
      return (
        <>
          <div className={classes.pageFooterDesktopButtons}>
            {showReview ? (
              renderSubmitAndBackButtons()
            ) : (
              <>
                <Button
                  variant={"outlined"}
                  className={clsx(
                    classes.pageFooterButton,
                    classes.pauseAndCloseButton
                  )}
                  onClick={saveAndClose()}
                >
                  SAVE & CLOSE
                </Button>
                <Button
                  className={clsx(
                    classes.pageFooterButton,
                    classes.pageFooterButtonNext
                  )}
                  variant={"contained"}
                  disableElevation
                  onClick={handleShowReviewClick}
                >
                  REVIEW FORM
                </Button>
              </>
            )}
          </div>
        </>
      );
    }
  };

  useEffect(() => {
    setHasUnsavedChanges(
      !isEqual(formik.values, tempState) ||
        !isEqual(new Set(originalStableEntries), new Set(pickedStableEntries))
    );
  }, [tempState, formik.values, pickedStableEntries]);

  useEffect(() => {
    if (hasUnsavedChanges) {
      lockNavigation();
    } else {
      unlockNavigation();
    }
  }, [hasUnsavedChanges]);

  useEffect(() => {
    return () => {
      unlockNavigation();
      clearLinkCandidate();
    };
  }, []);

  return (
    <AppPage className={classes.root}>
      <AppPageHeader className={classes.pageHeader}>
        {renderHeaderContent()}
      </AppPageHeader>
      {isLoading && <Loader overlay />}

      <AppPageContent className={classes.pageContent}>
        <FormikProvider value={formik}>
          {isFetching && <Loader overlay />}
          {showReview ? (
            <StallApplicationReview
              stableEntriesPreviewClassName={classes.scrollablePreviewContainer}
              applicationPreviewClassName={classes.scrollablePreviewContainer}
              values={values}
              stablingEntries={pickedStableEntries}
              facilityOptions={stallApplicationForm.facilityOptions}
            />
          ) : (
            <SwipeableTabs
              classes={{
                root: classes.tabsRoot,
                tabs: classes.tabs,
                tab: classes.tab,
                selected: classes.tabSelected,
                indicator: classes.tabsIndicator,
                views: classes.tabsViews,
                slideClassName: clsx(classes.tabSlide, {
                  popupView: currentUser.isTrainer
                }),
                content: classes.tabsContent
              }}
              activeTab={matchesDownSm ? activeTab : Tabs[0]}
              onTabChange={handleTabChange}
              disabled={!matchesDownSm}
              disableLazyLoading={true}
              tabs={Tabs}
            >
              <Form className={classes.form} onSubmit={formik.handleSubmit}>
                {renderTrainerDetails()}
              </Form>
              <div className={classes.stablingEntries}>
                <ErrorBoundary>
                  <StablingEntries
                    stallApplication={stateStallApplication}
                    originalStableEntries={originalStableEntries}
                    pickedStableEntries={pickedStableEntries}
                    isListStableEntriesFetching={isListStableEntriesFetching}
                    onChange={handleStableEntriesChange}
                    hasEntriesValidationError={hasEntriesValidationError}
                  />
                </ErrorBoundary>
              </div>
            </SwipeableTabs>
          )}
        </FormikProvider>
      </AppPageContent>
      <section className={classes.pageFooter}>
        {showReview && (
          <FormControlLabel
            className={classes.tncCheckbox}
            control={
              <Checkbox
                classes={{
                  checked: classes.tncCheckboxChecked
                }}
                color="primary"
                checked={tncAgree}
                onChange={handleTncAgreeCheck}
              />
            }
            label={
              <>
                I Agree to the{" "}
                <Link
                  className={classes.termsLink}
                  onClick={handleTermsAndConditionsLinkClick}
                >
                  Terms & Conditions
                </Link>
              </>
            }
            labelPlacement="end"
          />
        )}
        {renderFooter()}
      </section>
      {showTermsAndConditionsPopup && (
        <TermsAndConditionsPopup
          stallApplicationFormId={stallApplicationFormId}
          onClose={handleTermsAndConditionsPopupClose}
        />
      )}
      {showUnsavedChangesPopup && (
        <UnsavedChangesDialog
          onSave={saveAndClose()}
          onCancel={handleCancelUnsavedChanges}
          onDiscard={navigateAfterLeave}
          open={true}
          content={<span>{UNSAVED_CHANGES_TEXT}</span>}
          cancelButtonLabel={"Back"}
        />
      )}
    </AppPage>
  );
};

export default React.memo(StallApplicationFormPage);
